import util from 'preact-util';

// Helpers for pasting images (screenshots) into text fields.
// Logic mirrors the support-ticket paste handler (components/support/ticketView.js)
// so behaviour is consistent across the app. All functions are pure / stateless.

// Extract image files from a paste (clipboard) event.
// Clipboard screenshots often arrive without a filename, so we give them one.
// Returns an array of File objects (empty if the clipboard holds no images).
export function getImageFilesFromClipboard(e) {
    const clipboard = e.clipboardData || window.clipboardData;
    if (!clipboard) {
        return [];
    }
    const items = clipboard.items ? Array.from(clipboard.items) : [];
    const imageItems = items.filter(it => it.kind === 'file' && it.type && it.type.startsWith('image/'));
    return imageItems
        .map((it) => {
            const file = it.getAsFile();
            if (!file) {
                return null;
            }
            if (file.name) {
                return file;
            }
            const ext = (file.type && file.type.split('/')[1]) || 'png';
            return new File([file], `pasted-image-${Date.now()}.${ext}`, { type: file.type || 'image/png' });
        })
        .filter(Boolean);
}

// Upload a single image file to the standard file-upload endpoint.
// Resolves with the uploaded file object (with s3*Link fields), rejects on
// failure. Network-level failures (offline, timeout, connection reset)
// reject with err.isNetwork = true so callers can classify transient vs
// permanent; HTTP failures carry err.status.
export function uploadImageFile(file) {
    return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('files[]', file, encodeURIComponent(file.name));
        const xhr = new XMLHttpRequest();
        const failNetwork = (kind) => {
            const err = new Error(`Upload ${kind}`);
            err.isNetwork = true;
            reject(err);
        };
        xhr.timeout = 60000;
        xhr.addEventListener('error', () => failNetwork('network error'));
        xhr.addEventListener('timeout', () => failNetwork('timeout'));
        xhr.addEventListener('readystatechange', () => {
            if (xhr.readyState === 4) {
                if (xhr.status === 201) {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        const uploadedFile = response.data.files[0];
                        resolve(uploadedFile);
                    } catch (err) {
                        reject(err);
                    }
                } else if (xhr.status === 0) {
                    failNetwork('network failure');
                } else {
                    const err = new Error(`Upload failed: ${xhr.status}`);
                    err.status = xhr.status;
                    reject(err);
                }
            }
        });
        xhr.open('POST', `${util.getApiServer()}/api/fileupload`);
        xhr.setRequestHeader('Authorization', `Bearer ${util.getJwtToken()}`);
        xhr.send(formData);
    });
}

// Build a markdown image link for an uploaded file.
export function imageMarkdown(uploadedFile) {
    const imageUrl = uploadedFile.s3MediumLink || uploadedFile.s3Link || uploadedFile.s3SmallLink || uploadedFile.url || uploadedFile.src;
    const name = uploadedFile.name || 'image';
    return `![${name}](${imageUrl})`;
}

// Insert text at the cursor position of a textarea, then dispatch a bubbling
// 'input' event so the surrounding framework (and its autosave) picks up the change.
// Uses the native value setter so the value update is not swallowed by value tracking.
export function insertTextAtCursor(textarea, text) {
    const value = textarea.value || '';
    const start = typeof textarea.selectionStart === 'number' ? textarea.selectionStart : value.length;
    const end = typeof textarea.selectionEnd === 'number' ? textarea.selectionEnd : value.length;
    const before = value.slice(0, start);
    const after = value.slice(end);
    // Keep the image on its own line for clean markdown rendering.
    const prefix = before && !before.endsWith('\n') ? '\n' : '';
    const suffix = after && !after.startsWith('\n') ? '\n' : '';
    const insert = `${prefix}${text}${suffix}`;
    const newValue = before + insert + after;
    const cursorPos = start + insert.length;

    const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;
    setter.call(textarea, newValue);
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
    try {
        textarea.setSelectionRange(cursorPos, cursorPos);
    } catch (err) {
        // setSelectionRange can throw on detached/hidden nodes; safe to ignore.
    }
}
